import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { PageNotFoundComponent } from './layout/_global-errors/page-not-found/page-not-found.component';
import { RpaNotConnectedComponent } from './layout/_global-errors/rpa-not-connected/rpa-not-connected.component';
import { LayoutComponent } from './layout/layout.component';
import { AuthenticationGuard } from './shared/guards/authentication.guard';
import { MenuInitializationGuard } from './shared/guards/menu-initialization.guard';
import { OrganizationGuard } from './shared/guards/organization.guard';
import { PermissionsGuard } from './shared/guards/permissions.guard';
import { DefaultPageRedirectGuard } from './shared/guards/default-page-redirect.guard';
import { DefaultLoadingPageComponent } from './shared/components/default-loading-page/default-loading-page.component';
import { ModuleType, PageType, Tenants } from './shared/enums/page-type.enum';
import { checkRpaConnection } from './shared/guards/rpa-connection.guard';
import { SchedulerCacheInitializationGuard } from './shared/guards/scheduler-cache-initialization.guard';
import { TenantGuard } from './shared/guards/tenant.guard';
import { IsInitializedGuard } from './shared/guards/is-initialized.guard';
import { IsNotInitializedGuard } from './shared/guards/is-not-initialized.guard';

const routes: Routes = [
  {
    path: ModuleType.Auth,
    canActivate: [IsInitializedGuard],
    loadChildren: () => import('./modules/login/login.module').then(m => m.LoginModule),
  },
  {
    path: ModuleType.Initialization,
    canActivate: [IsNotInitializedGuard],
    loadChildren: () => import('./modules/initialization/initialization.module').then(m => m.InitializationModule),
  },
  {
    path: 'test-page',
    loadChildren: () => import('./modules/internal/test-page/test-page.module').then(m => m.TestPageModule),
  },
  {
    path: '',
    canActivate: [IsInitializedGuard, AuthenticationGuard],
    canDeactivate: [AuthenticationGuard],
    component: LayoutComponent,
    children: [
      {
        path: '',
        canActivate: [DefaultPageRedirectGuard],
        component: DefaultLoadingPageComponent,
      },
      {
        path: ModuleType.NotFound,
        component: PageNotFoundComponent,
        canActivate: [MenuInitializationGuard],
      },
      {
        path: ModuleType.Admin,
        canActivate: [PermissionsGuard, MenuInitializationGuard],
        loadChildren: () => import('./modules/admin-settings/admin-settings.module').then(m => m.AdminSettingsModule),
      },
      {
        path: PageType.DesignSystemOverview,
        loadChildren: () => import('./modules/internal/design-system-overview/design-system-overview.module').then(m => m.DesignSystemOverviewModule),
      },
      {
        path: ':organizationName',
        canActivate: [OrganizationGuard, SchedulerCacheInitializationGuard],
        children: [
          { path: '', pathMatch: 'full', redirectTo: Tenants.AllTenants },
          {
            path: ':tenantUrlName',
            canActivate: [TenantGuard, MenuInitializationGuard],
            children: [
              {
                path: PageType.RpaNotConnected,
                component: RpaNotConnectedComponent,
              },
              {
                path: '',
                canActivate: [DefaultPageRedirectGuard],
                component: DefaultLoadingPageComponent,
              },
              {
                path: ModuleType.Settings,
                canActivate: [PermissionsGuard],
                loadChildren: () => import('./modules/organization-settings/organization-settings.module').then(m => m.OrganizationSettingsModule),
              },
              {
                path: ModuleType.Workflows,
                canActivate: [PermissionsGuard],
                loadChildren: () => import('./modules/workflows/workflows.module').then(m => m.WorkflowsModule),
              },
              {
                path: ModuleType.Monitoring,
                canActivate: [PermissionsGuard, checkRpaConnection()],
                loadChildren: () => import('./modules/monitoring/monitoring.module').then(m => m.MonitoringModule),
              },
              {
                path: ModuleType.Orchestration,
                canActivate: [checkRpaConnection()],
                loadChildren: () => import('./modules/orchestration/orchestration.module').then(m => m.OrchestrationModule),
              },
              {
                path: ModuleType.Analytics,
                canActivate: [checkRpaConnection()],
                children: [
                  {
                    path: PageType.DetectedIncidents,
                    canActivate: [PermissionsGuard],
                    loadChildren: () => import('./modules/analytics/assistant/assistant.module').then(m => m.AssistantModule),
                  },
                  {
                    path: PageType.DiagnosticDashboard,
                    canActivate: [PermissionsGuard],
                    loadChildren: () => import('./modules/analytics/diagnostic-dashboard/diagnostic-dashboard.module').then(m => m.DiagnosticDashboardModule),
                  },
                  {
                    path: PageType.RuntimeAnalysis,
                    canActivate: [PermissionsGuard],
                    loadChildren: () => import('./modules/analytics/runtime-analysis/runtime-analysis.module').then(m => m.RuntimeAnalysisModule),
                  },
                  {
                    path: PageType.Anomalies,
                    canActivate: [PermissionsGuard],
                    loadComponent: () => import('./modules/analytics/anomalies/anomalies.component').then(m => m.AnomaliesComponent),
                  },
                ],
              },
              {
                path: ModuleType.Reporting,
                canActivate: [checkRpaConnection()],
                loadChildren: () => import('./modules/reporting/reporting.module').then(m => m.ReportingModule),
              },
            ],
          },
        ],
      },
      { path: '**', redirectTo: ModuleType.NotFound },
    ],
  },
  { path: '**', redirectTo: ModuleType.NotFound },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      anchorScrolling: 'enabled',
      scrollOffset: [0, 108],
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
